import type { EditorClip, EditorMask } from '../models/editor-clip.model';
import {
  hasMaskKeyframeAt,
  maskKeyframeTimes,
  removeMaskKeyframeAt,
  resolveMaskAt,
  setMaskKeyframeEaseAt,
  upsertMaskKeyframe,
  type MaskKeyframe,
  type ResolvedMask,
} from '../../../../core/render-runtime/shared/media-mask.util';
import type { KeyframeEase } from '../../../../core/render-runtime/shared/enter-exit-presets';

export const MASK_TIME_EPSILON = .001;

/** Read the new multi-mask collection, falling back to the legacy singular mask. */
export function editorMasks(clip: Pick<EditorClip, 'mask' | 'masks'> | null | undefined): EditorMask[] {
  if (clip?.masks?.length) return clip.masks.map(mask => ({ ...mask, points: mask.points?.map(point => ({ ...point })), keyframes: mask.keyframes?.map(frame => ({ ...frame })) }));
  return clip?.mask ? [{ ...clip.mask, points: clip.mask.points?.map(point => ({ ...point })), keyframes: clip.mask.keyframes?.map(frame => ({ ...frame })) }] : [];
}

export function maskRelativeTime(clip: Pick<EditorClip, 'startSeconds' | 'endSeconds'>, timelineSeconds: number): number {
  const duration = Math.max(0, clip.endSeconds - clip.startSeconds);
  return round3(Math.max(0, Math.min(duration, timelineSeconds - clip.startSeconds)));
}

export function resolveEditorMaskAt(clip: EditorClip, maskIndex: number, timelineSeconds: number): ResolvedMask | null {
  const mask = editorMasks(clip)[maskIndex];
  return mask ? resolveMaskAt(mask, maskRelativeTime(clip, timelineSeconds)) : null;
}

export function replaceEditorMask(clip: EditorClip, maskIndex: number, nextMask: EditorMask): EditorMask[] {
  return editorMasks(clip).map((mask, index) => index === maskIndex ? cloneMask(nextMask) : mask);
}

/** Direct panel/canvas edits become keyframes once this mask has been armed. */
export function applyEditorMaskEditAt(
  clip: EditorClip,
  maskIndex: number,
  timelineSeconds: number,
  patch: Partial<EditorMask>,
): EditorMask[] {
  const masks = editorMasks(clip);
  const mask = masks[maskIndex];
  if (!mask) return masks;
  if (!mask.keyframes?.length) {
    masks[maskIndex] = cloneMask({ ...mask, ...patch });
    return masks;
  }
  const relative = maskRelativeTime(clip, timelineSeconds);
  const resolved = resolveMaskAt(mask, relative);
  const value: EditorMask = { ...mask, ...resolved, ...patch };
  masks[maskIndex] = cloneMask({ ...mask, keyframes: upsertMaskKeyframe(mask, relative, value) });
  return masks;
}

export function toggleEditorMaskKeyframe(clip: EditorClip, maskIndex: number, timelineSeconds: number): EditorMask[] {
  const masks = editorMasks(clip);
  const mask = masks[maskIndex];
  if (!mask) return masks;
  const relative = maskRelativeTime(clip, timelineSeconds);
  const keyframes = hasMaskKeyframeAt(mask, relative)
    ? removeMaskKeyframeAt(mask, relative)
    : upsertMaskKeyframe(mask, relative, resolveMaskAt(mask, relative));
  masks[maskIndex] = cloneMask({ ...mask, keyframes });
  return masks;
}

export function editorMaskHasKeyframeAt(clip: EditorClip, maskIndex: number, timelineSeconds: number): boolean {
  const mask = editorMasks(clip)[maskIndex];
  return !!mask && hasMaskKeyframeAt(mask, maskRelativeTime(clip, timelineSeconds));
}

export function editorMaskKeyframeTimes(clip: EditorClip, maskIndex: number): number[] {
  const mask = editorMasks(clip)[maskIndex];
  return mask ? maskKeyframeTimes(mask) : [];
}

export function allEditorMaskKeyframes(clip: EditorClip): Array<{ maskIndex: number; maskId: string; frame: MaskKeyframe }> {
  return editorMasks(clip).flatMap((mask, maskIndex) => (mask.keyframes ?? []).map(frame => ({
    maskIndex,
    maskId: mask.id || `mask-${maskIndex + 1}`,
    frame: { ...frame },
  })));
}

export function setEditorMaskEaseAt(clip: EditorClip, maskId: string | null, tSeconds: number, ease: KeyframeEase): EditorMask[] {
  return editorMasks(clip).map((mask, index) => {
    if (maskId && (mask.id || `mask-${index + 1}`) !== maskId) return mask;
    if (!hasMaskKeyframeAt(mask, tSeconds)) return mask;
    return cloneMask({ ...mask, keyframes: setMaskKeyframeEaseAt(mask, tSeconds, ease) });
  });
}

export function removeEditorMaskKeyframesAt(clip: EditorClip, maskId: string | null, tSeconds: number): EditorMask[] | null {
  const next = editorMasks(clip).map((mask, index) => {
    if (maskId && (mask.id || `mask-${index + 1}`) !== maskId) return mask;
    return cloneMask({ ...mask, keyframes: removeMaskKeyframeAt(mask, tSeconds) });
  });
  return next.length ? next : null;
}

function cloneMask(mask: EditorMask): EditorMask {
  return {
    ...mask,
    points: mask.points?.map(point => ({ ...point })),
    keyframes: mask.keyframes?.map(frame => ({ ...frame })) ?? null,
  };
}

function round3(value: number): number { return Math.round(value * 1000) / 1000; }
